export const STORE_CART_MOQ_FETCH_DEBOUNCE_MS = 300;

export type StoreCartDesignGroupApiStatus = 'idle' | 'loading' | 'success' | 'error';

export interface IStoreCartDesignGroupApiStatus {
  quote: StoreCartDesignGroupApiStatus;
  moq: StoreCartDesignGroupApiStatus;
}

export const DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS: IStoreCartDesignGroupApiStatus = {
  quote: 'idle',
  moq: 'idle',
};

export const STORE_CART_DESIGN_GROUP_HELP_TOOLTIP = 'One design printed on multiple apparel';

export const STORE_CART_PRICES_UPDATED_TOOLTIP = 'Prices updated since your last visit.';

export const STORE_CART_PRICE_LOADING_ARIA_LABEL = 'Loading price';

export const STORE_CART_MOQ_LOADING_ARIA_LABEL = 'Loading minimum quantities';

export const STORE_CART_PRICE_INCREASED_WORD = 'increased';

export const STORE_CART_PRICE_DECREASED_WORD = 'decreased';

export const buildStoreCartPriceChangeWarningMessage = (
  direction: typeof STORE_CART_PRICE_INCREASED_WORD | typeof STORE_CART_PRICE_DECREASED_WORD
): string =>
  `Price has ${direction} since your last visit. Please review the updated pricing before proceeding.`;

export const STORE_CART_EMPTY_CTA_LABEL = 'Add items to create a store';

export const STORE_CART_SUMMARY_LABEL = 'Cart Summary';

/** Backend returns this as an error when the store cart has no items. */
export const STORE_CART_NO_ITEMS_API_MESSAGE = 'No cart items found';

export const STORE_CART_CREATE_STORE_ERROR_MSG =
  'Something went wrong while setting up your store.';

export const STORE_CART_AUTO_GENERATED_DESIGN_GROUP_DESCRIPTION_PREFIX = 'Auto-generated group for';

export const buildStoreCartAutoGeneratedDesignGroupDescription = (
  designGroupName: string
): string => `${STORE_CART_AUTO_GENERATED_DESIGN_GROUP_DESCRIPTION_PREFIX} ${designGroupName}`;

export const STORE_CART_DESIGNS_MODAL_HEADER = 'Pick the Designs You Want to Add to Store';

export const REGULAR_CART_DESIGNS_MODAL_HEADER = 'Pick the Designs You Want to Add to Cart';

export const STORE_CART_UI_VARIATION_VALUES = {
  PHASE_2: 'phase-2',
  FINAL_FLOW: 'final-flow',
} as const;

export type StoreCartUiVariation =
  (typeof STORE_CART_UI_VARIATION_VALUES)[keyof typeof STORE_CART_UI_VARIATION_VALUES];

export const STORE_CART_UI_VARIATION_OPTIONS: { label: string; value: StoreCartUiVariation }[] = [
  { label: '#phase-2', value: STORE_CART_UI_VARIATION_VALUES.PHASE_2 },
  { label: '#final flow', value: STORE_CART_UI_VARIATION_VALUES.FINAL_FLOW },
];

export const shouldHideStoreCartAddOptionButtons = (variation: StoreCartUiVariation): boolean =>
  variation === STORE_CART_UI_VARIATION_VALUES.PHASE_2;

export const STORE_CART_ADD_COLOR_OPTION_ARIA_LABEL = 'Add color option in design tool';

export const STORE_CART_ADD_PRODUCT_OPTION_ARIA_LABEL = 'Add product option in design tool';

export const STORE_CART_OPTION_CANCEL_ICON_SRC = '/src/assets/svg/tick_option.svg';

export const STORE_CART_OPTION_RESELECT_ICON_SRC = '/src/assets/svg/plus_option.svg';

const STORE_CART_REVIEW_SVG_ASSETS = '/src/assets/svg/store-review';

export const STORE_CART_IMAGE_CAROUSEL_CHEVRON_LEFT_SRC = `${STORE_CART_REVIEW_SVG_ASSETS}/chevron-left.svg`;

export const STORE_CART_IMAGE_CAROUSEL_CHEVRON_RIGHT_SRC = `${STORE_CART_REVIEW_SVG_ASSETS}/chevron-right.svg`;

export const STORE_CART_IMAGE_CAROUSEL_NAV_PREVIOUS_ARIA_LABEL = 'Previous image';

export const STORE_CART_IMAGE_CAROUSEL_NAV_NEXT_ARIA_LABEL = 'Next image';

export const STORE_CART_DESELECT_COLOR_OPTION_ARIA_LABEL = 'Remove color option from store';

export const STORE_CART_SELECT_COLOR_OPTION_ARIA_LABEL = 'Add color option to store';

export const STORE_CART_DESELECT_PRODUCT_OPTION_ARIA_LABEL = 'Remove product option from store';

export const STORE_CART_SELECT_PRODUCT_OPTION_ARIA_LABEL = 'Add product option to store';

export const STORE_CART_ADD_COLOR_OPTION_ERROR_MSG = 'Unable to add color option to store cart';

export const STORE_CART_OPTION_INCLUSION_ERROR_MSG =
  'Unable to save store option selection. Please try again.';

export const STORE_CART_ADD_PRODUCT_OPTION_ERROR_MSG = 'Unable to add product option to store cart';

export const STORE_CART_DESIGN_DETAILS_NAV_ERROR_MSG = 'Failed to navigate to design details page';

export const STORE_CART_DESIGN_TOOL_COLOR_NAV_ERROR_MSG =
  'Unable to open color options in the design tool';

export const STORE_CART_DESIGN_TOOL_PRODUCT_NAV_ERROR_MSG =
  'Unable to open product options in the design tool';

export const STORE_CART_OPEN_QUERY_KEY = 'storeCartOpen';

/** Matches `MOBILE_MAX_WIDTH` in useViewport — mobile uses the drawer overlay, not the page. */
export const STORE_CART_PAGE_MIN_WIDTH = 621;

/** Matches `TAB_MAX_WIDTH` in useViewport — tablet uses the full-page store cart UI. */
export const STORE_CART_PAGE_MAX_WIDTH = 992;

export const STORE_CART_DESIGN_NAV_QUERY_KEYS = {
  ADD_OPTION: 'storeCartAddOption',
  STYLE_CODE: 'storeCartStyleCode',
} as const;

export const STORE_CART_DESIGN_ADD_OPTION = {
  COLOR: 'color',
  PRODUCT: 'product',
} as const;

export type StoreCartDesignAddOption =
  (typeof STORE_CART_DESIGN_ADD_OPTION)[keyof typeof STORE_CART_DESIGN_ADD_OPTION];

export const STORE_MODAL_TOOLTIP_Z_INDEX = 10050;
